import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Check, Hash } from 'lucide-react';
import Avatar from '@/components/comms/Avatar';
import clsx from 'clsx';
import type { Person } from '@/utils/commsHelpers';

interface Props {
  open: boolean;
  people: Person[];
  /** Ids never offered in the picker (e.g. the creator — always a member). */
  excludeIds?: string[];
  onClose: () => void;
  onCreate: (name: string, memberIds: string[]) => void;
}

/**
 * Name a new channel and add people. Combines the form shell of AddStudioModal
 * (name field + Cancel/Create) with a multi-select adaptation of PersonPicker.
 */
export default function CreateChannelModal({
  open,
  people,
  excludeIds = [],
  onClose,
  onCreate,
}: Props) {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !exclude.has(p.id))
      .filter((p) => p.id !== 'system-bot')
      .filter((p) => !q || p.displayName.toLowerCase().includes(q) || p.role.toLowerCase().includes(q))
      .slice(0, 30);
  }, [query, people, exclude]);

  const canCreate = name.trim() !== '';

  const reset = () => {
    setName('');
    setQuery('');
    setSelected(new Set());
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const create = () => {
    if (!canCreate) return;
    onCreate(name.trim(), [...selected]);
    reset();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-start justify-center pt-24 px-6"
          onClick={close}
          data-testid="comms-create-channel"
        >
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md panel p-0 overflow-hidden"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-gold-dim/15">
              <div>
                <p className="label-small-caps text-gold-bright">Create channel</p>
                <p className="font-serif text-cream-soft text-xs mt-1">
                  Name the channel and add people. You are added automatically.
                </p>
              </div>
              <button type="button" onClick={close} className="btn-quiet p-1" aria-label="Close">
                <X size={12} />
              </button>
            </header>

            <div className="px-4 py-3 border-b border-gold-dim/15">
              <label className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                Channel name
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-md border border-gold-dim/40 bg-ink-deep px-2.5 py-1.5 focus-within:border-gold-bright">
                <Hash size={12} className="text-cream-faint" />
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') create();
                  }}
                  placeholder="e.g. melbourne-ops"
                  className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
                  data-testid="comms-create-channel-name"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 px-4 py-3 border-b border-gold-dim/15">
              <Search size={12} className="text-cream-faint" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Add people — search by name or role…"
                className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
                data-testid="comms-create-channel-search"
              />
              {selected.size > 0 ? (
                <span className="font-mono text-[11px] text-gold-bright">{selected.size} added</span>
              ) : null}
            </div>

            <ul className="max-h-64 overflow-y-auto">
              {matches.length === 0 ? (
                <li className="px-4 py-3 font-serif text-cream-soft text-sm">No matches.</li>
              ) : null}
              {matches.map((p) => {
                const on = selected.has(p.id);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => toggle(p.id)}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-ink-soft/60 transition"
                      data-testid={`comms-create-channel-person-${p.id}`}
                      aria-pressed={on}
                    >
                      <Avatar person={p} size="md" />
                      <span className="flex-1">
                        <span className="block font-inter text-sm text-cream">{p.displayName}</span>
                        <span className="block font-inter text-[9px] uppercase tracking-widest text-cream-faint mt-0.5">
                          {p.role}
                          {p.studioId ? ` · ${p.studioId.replace('-', ' ')}` : ''}
                        </span>
                      </span>
                      <span
                        className={clsx(
                          'flex h-5 w-5 items-center justify-center rounded-md border transition',
                          on
                            ? 'border-gold-bright bg-gold-bright text-ink-black'
                            : 'border-gold-dim/40 text-transparent',
                        )}
                      >
                        <Check size={12} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gold-dim/15">
              <button type="button" onClick={close} className="btn-quiet text-xs">
                Cancel
              </button>
              <button
                type="button"
                onClick={create}
                disabled={!canCreate}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-inter uppercase tracking-widest border transition',
                  canCreate
                    ? 'border-gold-bright text-gold-bright bg-gold/10 hover:bg-gold/20'
                    : 'border-gold-dim/30 text-cream-faint cursor-not-allowed',
                )}
                data-testid="comms-create-channel-submit"
              >
                Create
              </button>
            </footer>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
